/**
 * Post-hoc text-distance verification of OOXML recovery (see
 * `ooxml-recovery.ts`). Treats the redline backend's TARGET body as
 * source of truth and Levenshtein-compares its visible-text stream
 * against the live post-recovery body.
 */

const PARSE_NS_WRAPPER_HEAD = '<root xmlns:w="http://schemas.openxmlformats.org/wordprocessingml/2006/main">';
const PARSE_NS_WRAPPER_TAIL = '</root>';

const PKG_NS = "http://schemas.microsoft.com/office/2006/xmlPackage";

export function extractBodyContent(ooxml: string): string {
    if (!ooxml) return "";

    const parser = new DOMParser();
    const doc = parser.parseFromString(ooxml, "application/xml");

    if (doc.getElementsByTagName("parsererror").length > 0) {
        return ooxml;
    }

    let body = doc.getElementsByTagName("w:body")[0];

    if (!body) {
        const documentParts = Array.from(doc.getElementsByTagNameNS(PKG_NS, "part"))
            .filter(p => (p.getAttribute("pkg:name") || "").endsWith("/document.xml"));
        for (const part of documentParts) {
            const xmlData = part.getElementsByTagNameNS(PKG_NS, "xmlData")[0];
            if (!xmlData) continue;
            const inner = Array.from(xmlData.getElementsByTagName("w:body"))[0];
            if (inner) { body = inner; break; }
        }
    }

    if (!body) return ooxml;

    const serializer = new XMLSerializer();
    return Array.from(body.childNodes).map(n => serializer.serializeToString(n)).join("");
}

export function truncateForSentry(text: string, maxChars = 2000): string {
    if (text.length <= maxChars) return text;
    const half = Math.floor(maxChars / 2);
    return `${text.slice(0, half)}\n…[${text.length - maxChars} chars elided]…\n${text.slice(text.length - half)}`;
}

/**
 * Visible-text stream from a `<w:body>`-inner fragment: concatenated
 * `<w:t>` + `<w:delText>` in document order (track-changes ON view).
 * Falls back to the raw input on parse error so the caller still gets
 * a number to threshold against.
 */
export function extractVisibleText(bodyXml: string): string {
    if (!bodyXml) return "";

    const parser = new DOMParser();
    const wrapped = `${PARSE_NS_WRAPPER_HEAD}${bodyXml}${PARSE_NS_WRAPPER_TAIL}`;
    const doc = parser.parseFromString(wrapped, "application/xml");

    if (doc.getElementsByTagName("parsererror").length > 0) {
        return bodyXml;
    }

    const parts: string[] = [];
    const walk = (node: Node) => {
        if (node.nodeType === 1) {
            const el = node as Element;
            const local = el.localName;
            if (local === "t" || local === "delText") {
                parts.push(el.textContent || "");
                return;
            }
        }
        for (let child = node.firstChild; child; child = child.nextSibling) {
            walk(child);
        }
    };
    walk(doc.documentElement);

    return parts.join("");
}

export interface RedlineSignature {
    ins: number;
    del: number;
    insTextless: number;
    delTextless: number;
    insText: string;
    delText: string;
}

const carriesDescendant = (el: Element, localName: string): boolean =>
    el.getElementsByTagNameNS("*", localName).length > 0;

export function redlineSignature(bodyXml: string): RedlineSignature {
    const ledger = {
        ins: 0,
        del: 0,
        insTextless: 0,
        delTextless: 0,
        insParts: [] as string[],
        delParts: [] as string[],
    };
    if (bodyXml) {
        const parser = new DOMParser();
        const wrapped = `${PARSE_NS_WRAPPER_HEAD}${bodyXml}${PARSE_NS_WRAPPER_TAIL}`;
        const doc = parser.parseFromString(wrapped, "application/xml");
        if (doc.getElementsByTagName("parsererror").length === 0) {
            const walk = (node: Node, insideInsertion: boolean) => {
                if (node.nodeType === 1) {
                    const el = node as Element;
                    if (el.localName === "ins") {
                        ledger.ins++;
                        if (!carriesDescendant(el, "t")) ledger.insTextless++;
                        insideInsertion = true;
                    }
                    if (el.localName === "del") {
                        ledger.del++;
                        if (!carriesDescendant(el, "delText")) ledger.delTextless++;
                    }
                    if (el.localName === "t" && insideInsertion) {
                        ledger.insParts.push(el.textContent || "");
                        return;
                    }
                    if (el.localName === "delText") {
                        ledger.delParts.push(el.textContent || "");
                        return;
                    }
                }
                for (let child = node.firstChild; child; child = child.nextSibling) {
                    walk(child, insideInsertion);
                }
            };
            walk(doc.documentElement, false);
        }
    }
    return {
        ins: ledger.ins,
        del: ledger.del,
        insTextless: ledger.insTextless,
        delTextless: ledger.delTextless,
        insText: ledger.insParts.join(""),
        delText: ledger.delParts.join(""),
    };
}

export function redlinesDropped(expected: RedlineSignature, actual: RedlineSignature): boolean {
    if (actual.insText.length < expected.insText.length - RECOVERY_DRIFT_THRESHOLD) return true;
    if (actual.delText.length < expected.delText.length - RECOVERY_DRIFT_THRESHOLD) return true;
    if (actual.insTextless < expected.insTextless) return true;
    return actual.delTextless < expected.delTextless;
}

const VERIFY_DISTANCE_LIMIT = 200;

function levenshteinDistance(a: string, b: string, limit: number): number {
    if (a === b) return 0;
    const m = a.length;
    const n = b.length;
    if (m === 0) return Math.min(n, limit + 1);
    if (n === 0) return Math.min(m, limit + 1);
    if (m - n > limit || n - m > limit) return limit + 1;

    let prev: number[] = new Array(n + 1).fill(Number.POSITIVE_INFINITY);
    let curr: number[] = new Array(n + 1).fill(Number.POSITIVE_INFINITY);
    for (let j = 0; j <= Math.min(n, limit); j++) prev[j] = j;

    for (let i = 1; i <= m; i++) {
        const from = Math.max(1, i - limit);
        const to = Math.min(n, i + limit);
        curr.fill(Number.POSITIVE_INFINITY);
        if (i <= limit) curr[0] = i;
        const ai = a.charCodeAt(i - 1);
        let rowMinimum = curr[0];
        for (let j = from; j <= to; j++) {
            const cost = ai === b.charCodeAt(j - 1) ? 0 : 1;
            curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
            rowMinimum = Math.min(rowMinimum, curr[j]);
        }
        if (rowMinimum > limit) return limit + 1;
        [prev, curr] = [curr, prev];
    }

    return Math.min(prev[n], limit + 1);
}

/** First index where two strings diverge, with a windowed snippet for
 *  logs/Sentry. Returns null when identical; on a strict-prefix
 *  mismatch annotates the short side with `<end>`. */
export function describeFirstDivergence(
    expected: string,
    actual: string,
    contextChars = 30,
): string | null {
    if (expected === actual) return null;

    let i = 0;
    const minLen = Math.min(expected.length, actual.length);
    while (i < minLen && expected.charCodeAt(i) === actual.charCodeAt(i)) i++;

    const snippet = (text: string, at: number): string => {
        const start = Math.max(0, at - contextChars);
        const end = Math.min(text.length, at + contextChars);
        const head = start > 0 ? "…" : "";
        const tail = end < text.length ? "…" : "";
        const before = text.slice(start, at);
        const atChar = at < text.length ? text.charAt(at) : "<end>";
        const after = text.slice(at + 1, end);
        return `${head}${before}»${atChar}«${after}${tail}`;
    };

    return `first diff @${i}/${Math.max(expected.length, actual.length)} ` +
        `(expected len=${expected.length}, actual len=${actual.length}): ` +
        `expected=${JSON.stringify(snippet(expected, i))}, ` +
        `actual=${JSON.stringify(snippet(actual, i))}`;
}

/** Character-distance threshold under which we silently accept the
 *  recovery. 1 = tolerate one autocorrect-class char (apostrophe
 *  variant, en/em dash, NBSP) without warning the user. */
export const RECOVERY_DRIFT_THRESHOLD = 1;

export type RecoveryOutcome =
    | { status: "clean"; distance: number }
    | { status: "drift"; distance: number; expectedText: string; actualText: string; reason: string };

/** Compute the recovery outcome from desired (TARGET) and live bodies.
 *  `actualBody === null` ⇒ snapshot failed; reported as drift with a
 *  dedicated reason so Sentry distinguishes edit-drift from no-verify. */
export function verifyRecovery(args: {
    targetBody: string;
    actualBody: string | null;
}): RecoveryOutcome {
    const { targetBody, actualBody } = args;
    const expectedText = extractVisibleText(targetBody);

    if (actualBody === null) {
        return {
            status: "drift",
            distance: -1,
            expectedText,
            actualText: "",
            reason: "could not snapshot the post-recovery document range to verify",
        };
    }

    const actualText = extractVisibleText(actualBody);
    const distance = levenshteinDistance(expectedText, actualText, VERIFY_DISTANCE_LIMIT);

    if (distance <= RECOVERY_DRIFT_THRESHOLD) {
        return { status: "clean", distance };
    }

    const edits = distance > VERIFY_DISTANCE_LIMIT
        ? `more than ${VERIFY_DISTANCE_LIMIT} edits`
        : `${distance} edit${distance === 1 ? '' : 's'}`;
    return {
        status: "drift",
        distance,
        expectedText,
        actualText,
        reason: `text differs from TARGET by ${edits} (threshold ${RECOVERY_DRIFT_THRESHOLD})`,
    };
}
